import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  FaFlag, 
  FaShieldAlt, 
  FaGamepad, 
  FaHandsHelping, 
  FaUsers, 
  FaChartLine, 
  FaRocket 
} from 'react-icons/fa';

const timelineData = [
  {
    year: "2009",
    title: "Enlisted in the US Navy",
    description: "Embarked on a distinguished career as an Intelligence Analyst, dedicating 12 years to service.",
    icon: <FaFlag size={24} />,
  },
  {
    year: "2016",
    title: "Joined Naval Special Warfare Community",
    description: "Advanced my expertise in intelligence and analytics through rigorous training and multiple deployments.",
    icon: <FaShieldAlt size={24} />,
  },
  {
    year: "2021",
    title: "Transitioned to Product Management at Blizzard Entertainment",
    description: "Assumed the role of Senior Product Manager, leading the development of an Enterprise Data Platform solution.",
    icon: <FaGamepad size={24} />,
  },
  {
    year: "2022",
    title: "Founded a Nonprofit for Military Veterans",
    description: "Established a nonprofit organization, developing a custom web application to support career discovery and provide resources for military personnel, veterans, and their families.",
    icon: <FaHandsHelping size={24} />,
  },
  {
    year: "2023",
    title: "Veteran Community Initiative",
    description: "Partnered and served as Product Director for an industry-leading veteran nonprofit program, enhancing community support and engagement.",
    icon: <FaUsers size={24} />,
  },
  {
    year: "2023",
    title: "Joined Electronic Arts",
    description: "Accepted a full-time position as Data Governance Manager at EA, focusing on building a robust data culture and enhancing data governance frameworks.",
    icon: <FaGamepad size={24} />,
  },
  {
    year: "2024",
    title: "Promoted to Senior Data Governance Manager",
    description: "Led results-driven data governance initiatives, culminating in a promotion within one year at EA.",
    icon: <FaChartLine size={24} />,
  },
  {
    year: "2024",
    title: "Founded a Tech Startup",
    description: "Leveraging my expertise in military intelligence, product management, web development, nonprofit leadership, and data governance, I founded a tech startup to develop innovative products. Coming soon!",
    icon: <FaRocket size={24} />,
  },
];

export default function HerosJourneyTimeline() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    // Initialize the ref array
    sectionRefs.current = sectionRefs.current.slice(0, timelineData.length);

    const observerOptions = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.5, // 50% visibility
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = Number((entry.target as HTMLElement).dataset.index);
          setActiveIndex(index);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      if (observer) {
        sectionRefs.current.forEach((ref) => {
          if (ref) observer.unobserve(ref);
        });
      }
    };
  }, []);

  return (
    <section className="relative bg-gray-900 text-white pb-20">
      <div className="flex flex-col md:flex-row">
        {/* Left Column: Sticky Timeline */}
        <div className="w-full md:w-1/3 sticky top-0 h-[90vh] flex flex-col items-center px-4 py-12">
          <h2 className="text-4xl font-bold mb-6 text-center">My Journey</h2>
          
          {/* Relative container for line and icons */}
          <div className="relative flex-1 bg-gray-700 h-full py-8 pb-16"> {/* Added pb-16 for bottom padding */}
            {/* Animated Progress Line */}
            <motion.div
              className="absolute left-1/2 bg-orange-500 w-px z-0 origin-top"
              initial={{ height: '0%' }}
              animate={{ height: `${activeIndex * (100 / (timelineData.length -1))}%` }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              style={{ top: '4rem' }} // top-16 = 4rem
            />

            {/* Animated Light/Bounce */}
            <motion.div
              className="absolute left-1/2 z-10"
              style={{ 
                top: `${(activeIndex / (timelineData.length -1)) * 100}%`, 
                transform: 'translate(-50%, -50%)' // Center vertically and horizontally
              }}
              animate={{ 
                top: `${(activeIndex / (timelineData.length -1)) * 100}%` 
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="w-6 h-6 bg-yellow-300 rounded-full animate-pulse" aria-hidden="true" />
            </motion.div>

            {/* Timeline Icons */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 flex flex-col justify-between h-full w-full py-4">
              {timelineData.map((event, index) => (
                <div
                  key={index}
                  className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center"
                  style={{ top: `${(index / (timelineData.length -1)) * 100}%` }}
                >
                  <motion.div
                    className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-500 ${
                      activeIndex >= index ? 'bg-orange-500 text-white' : 'bg-gray-800'
                    }`}
                    whileHover={{ scale: 1.2 }}
                    style={{ zIndex: 20, position: 'relative' }}
                    role="button"
                    aria-label={`Timeline event: ${event.title}`}
                    tabIndex={0}
                  >
                    {event.icon}
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Content Sections */}
        <div className="w-full md:w-2/3">
          {timelineData.map((event, index) => (
            <div
              key={index}
              data-index={index}
              ref={(el: HTMLDivElement | null) => {
                if (el && sectionRefs.current) {
                  sectionRefs.current[index] = el;
                }
              }}
              className="h-[90vh] flex flex-col justify-center p-12"
            >
             <div className="relative bg-gray-800 p-6 rounded-lg shadow-lg z-10 max-w-[600px]">
              <h3
                className={`text-3xl font-semibold transition-colors duration-500 ${
                  activeIndex === index ? 'text-orange-500' : 'text-gray-400'
                }`}
              >
                {event.year}
              </h3>
              <h4 className="text-2xl mt-4">{event.title}</h4>
              <p className="text-gray-300 mt-2 max-w-2xl">{event.description}</p>
             </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
